//Imports
import React, { useCallback, useEffect, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    Button,
    Form,
    Input,
    Modal
} from "@gpstrackit/gtc-react-controls";

//Components
import {
    NotificationManager,
    NotificationContainer,
} from "Components/Notifications";

//Slice
import {
    manageAssetSubtypeEditAssetSubtype,
    manageAssetSubtypeCreateAssetSubtype,
    manageAssetSubtypeGetAssetSubtype,
    reset,
} from "./redux/slice";

import { cleanString } from 'Core/data/Helpers';

const INIT_DATA = {
    name: undefined
};

const CreateEditManageAssetSubtype = ({ refresh }) => {

    //Local States
    const [formData, setFormData] = useState(INIT_DATA);
    const { messages } = useIntl();

    //Form refs
    const createEditAssetSubtypeFormRef = Form.useForm();

    //Redux States
    const { loading, open, create, update, error, assetSubtype, assetSubtypeId } = useSelector(state => state.manageCreateEditAssetSubtypeRedux);

    const dispatch = useDispatch();

    //Handlers and utils
    const handleClose = () => {
        setFormData(INIT_DATA);
        dispatch(reset());
    };

    const handleSuccess = () => {
        let message = assetSubtypeId ? messages["manage_createEditAssetSubtype_successMessageUpdate"] : messages["manage_createEditAssetSubtype_successMessageCreate"];
        NotificationManager.success(
            message,
            messages["success"],
            3000
        );
        refresh();
        handleClose();
    };

    const applyChanges = useCallback(() => {
        const { ...otherFormData } = formData || {};
        const payload = {
            ...otherFormData
        };

        if (assetSubtypeId) {
            dispatch(manageAssetSubtypeEditAssetSubtype(payload));
        } else {
            dispatch(manageAssetSubtypeCreateAssetSubtype(payload));
        }
    }, [formData, assetSubtypeId, dispatch]);

    useEffect(() => {
        if (create || update) {
            handleSuccess();
        }
    }, [create, update]);

    useEffect(() => {
        if (error) {
            let currentError = error?.error
            if (currentError.includes("duplicated")) {
                currentError = messages['manage_createEditAssetSubtype_duplicatedName']
            }
            else if (currentError.includes("are in use")) {
                currentError = messages['manage_createEditAssetSubtype_are_in_use'];
            }
            NotificationManager.error(
                currentError || messages["errorMessage"],
                messages["error"],
                3000
            );
            dispatch(reset({ error: null }));
        }
    }, [error])

    useEffect(() => {
        if (assetSubtypeId) {
            dispatch(manageAssetSubtypeGetAssetSubtype({ id: assetSubtypeId }))
        }
    }, [assetSubtypeId])

    useEffect(() => {
        if (assetSubtype && open) {
            let payload = {
                ...assetSubtype
            }
            setFormData(payload);
            createEditAssetSubtypeFormRef.setValues(payload);
        }
    }, [assetSubtype, open])

    return (
        <Modal
            title={assetSubtypeId ? `${messages['manage_createEditAssetSubtype_editTitle']} ${assetSubtype?.name ? `"${assetSubtype?.name}"` : ""}` : messages['manage_createEditAssetSubtype_createTitle']}
            open={open}
            handleClose={handleClose}
            position="right"
            style={{ zIndex: "auto" }}
            size="small"
            loading={loading}
            footer={
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading}
                        onClick={() => {
                            createEditAssetSubtypeFormRef?.submit();
                        }}
                        variant="primary"
                    >
                        {messages["save"]}
                    </Button>
                </div>
            }
        >
            <div style={{ padding: 24 }}>
                <Form
                    ref={createEditAssetSubtypeFormRef.ref}
                    loading={false}
                    onValidSubmit={() => applyChanges()}
                    onChangeValues={() => { }}
                >
                    <Form.Item
                        name="name"
                        label={messages['manage_createEditAssetSubtype_name']}
                        rules={[
                            { required: true, message: messages["required_field"] },
                            (_, newValue) => {
                                const value = newValue?.toString().trim();
                                if (!value) {
                                    return messages["required_field"];
                                }
                            }
                        ]}
                    >
                        <Input
                            id="name"
                            type="text"
                            placeholder={messages['manage_createEditAssetSubtype_name']}
                            onChange={(value) => {
                                const cleanedValue = cleanString(value);
                                setFormData({ ...formData, name: cleanedValue })
                                if (cleanedValue !== value) {
                                    setTimeout(() => {
                                        createEditAssetSubtypeFormRef.setValues({ name: cleanedValue });
                                    }, 100);
                                }
                            }}
                            maxLength={32}
                        />
                    </Form.Item>
                </Form>
            </div>
            <NotificationContainer />
        </Modal>
    );
};

export default CreateEditManageAssetSubtype;